import { inject, RESPONSE_INIT } from '@angular/core';
import { ParamMap, ResolveFn } from '@angular/router';
import { catchError, map, of } from 'rxjs';

import { SeoService } from '../../../shared/services/seo.service';
import {
  applyBlogSeoSafely,
  buildBlogListSeo,
  buildBlogLoadErrorSeo,
  setBlogLoadFailureStatus,
} from '../utils/blog-seo.util';
import { BlogService } from './blog.service';
import {
  BLOG_LOAD_ERROR,
  isBlogLoadError,
  type BlogListFilters,
  type BlogListPage,
  type BlogLoadError,
} from './blog-view.model';

/** Filtros y página del listado según sus query params (B65). */
export interface BlogListQuery {
  filters: BlogListFilters;
  page: number;
}

export function readBlogListQuery(params: ParamMap): BlogListQuery {
  const page = Math.floor(Number(params.get('page')));
  return {
    filters: {
      game: params.get('game') || undefined,
      category: params.get('category') || undefined,
      tag: params.get('tag') || undefined,
      search: params.get('search') || undefined,
    },
    page: page >= 1 ? page : 1,
  };
}

/**
 * Resuelve la página de `/blog` y aplica su SEO ANTES de activar el listado
 * (B66, mismo patrón que `blogArticleResolver`): el HTML del SSR de
 * `/blog?game=one-piece` ya sale con su título, canonical y robots.
 *
 * La ruta lo declara con `runGuardsAndResolvers: 'paramsOrQueryParamsChange'`,
 * así que filtrar o paginar en cliente lo vuelve a ejecutar y los tags siguen
 * a la URL. La página lee el resultado de `route.data['list']`.
 *
 * Si la API no responde (B123, B132 a): `BLOG_LOAD_ERROR`, `noindex,nofollow`
 * y, en SSR, 503 + `Retry-After` (500 sin `Retry-After` si el error es interno,
 * Astra W5, B2). Sin esto, el error de navegación acabaría en
 * el 404 genérico de Express. Solo la llamada a la API decide el 503 (Astra
 * W5, A1): un fallo de SEO se registra y la página se pinta igual.
 */
export const blogListResolver: ResolveFn<BlogListPage | BlogLoadError> = (route) => {
  const blog = inject(BlogService);
  const seo = inject(SeoService);
  const responseInit = inject(RESPONSE_INIT, { optional: true });
  const { filters, page } = readBlogListQuery(route.queryParamMap);

  return blog.list(filters, page).pipe(
    // Solo los errores del servicio llegan aquí; el SEO va después (A1). El
    // estado HTTP depende de quién falló (B2): fuente → 503 + Retry-After;
    // error interno → 500, registrado.
    catchError((err: unknown) => {
      setBlogLoadFailureStatus(responseInit, err);
      return of(BLOG_LOAD_ERROR);
    }),
    map((result) => {
      if (isBlogLoadError(result)) {
        applyBlogSeoSafely(seo, buildBlogLoadErrorSeo);
        return result;
      }
      applyBlogSeoSafely(seo, () => buildBlogListSeo(filters, result.applied, result.page));
      return result;
    }),
  );
};
